import { FC } from "react";
import { Box, Skeleton, useTheme } from "@mui/material";
import { observer } from "mobx-react";

import { IMessage } from "../../../../services";
import Row from "../../../ui/core/Row";
import Column from "../../../ui/core/Column";
import Avatar from "../../../ui/Avatar";
import MarkdownRenderer from "../../../ui/MarkdownRenderer";
import { ChatHelper } from "../../../helpers/chatHelper";
import { useChatStore } from "../../../../hooks/useChatStore";
import { ChatMessageActions } from "./ChatMessageActions";
import ChatMessageLetterBox from "./ChatMessageLetterBox";
import ChatMessageClaimDetail from "./ChatMessageClaimDetail";
import ChatMessageAppealStrategy from "./ChatMessageAppealStrategy";
import ChatMessageUnavailable from "./ChatMessageUnavailable";
import {
  IMessageAppealStrategy,
  IMessageClaimDetail,
  IMessageMetadata,
} from "../../../../types/chat";

// REV-2299: explicit per-agent card routing instead of an implicit
// "appeal_strategy or else claim detail" ternary, so adding a new agent means
// adding a case here rather than silently falling through to the wrong card.
// claim_reviewer is the historical default and stays the fallback for any
// agent_name not listed (e.g. appeal_letter_writer's non-letter content,
// or an agent not yet given a dedicated card) — ChatMessageClaimDetail
// renders unrecognized sections generically rather than dropping them.
const renderCardPart = (
  agentName: string | undefined,
  content: unknown,
  metadata: IMessageMetadata | undefined,
  key: string,
) => {
  switch (agentName) {
    case "appeal_strategy":
      return (
        <ChatMessageAppealStrategy
          key={key}
          data={content as IMessageAppealStrategy}
          metadata={metadata}
        />
      );
    case "claim_reviewer":
    default:
      return (
        <ChatMessageClaimDetail
          key={key}
          data={content as IMessageClaimDetail}
          metadata={metadata}
        />
      );
  }
};

// The backend's text on a letter's own message, e.g. "Appeal letter 3 generated".
const LETTER_MESSAGE_TEXT = /^[\w ]+ \d+ generated$/;

interface ChatServerMessageProps {
  message: IMessage;
  isCrossUserMessage?: boolean;
}

const ChatServerMessage: FC<ChatServerMessageProps> = observer(({
  message,
  isCrossUserMessage,
}) => {
  const theme = useTheme();
  const chatStore = useChatStore();

  const letter = message.id
    ? chatStore.chatLetters.find((item) => item.message === message.id)
    : null;
    
  const messageContent =
    typeof message?.data === "string"
      ? message?.data
      : JSON.stringify(message?.data);

  const contentParts = ChatHelper.parseMessageContentParts(messageContent);
  // The letter's button says it; while letters load, hide it rather than flash it.
  // Any other text on a letter's message is real and stays.
  const hideLetterMessageText =
    LETTER_MESSAGE_TEXT.test(messageContent.trim()) &&
    (!!letter || chatStore.isChatLettersLoading);
  // Nothing else would show in the bubble until the letters arrive, so hold the
  // letter button's place instead of rendering an empty bubble.
  const showLetterPlaceholder =
    !letter &&
    chatStore.isChatLettersLoading &&
    (contentParts.length === 0 || hideLetterMessageText);

  return (
    <Row
      sx={{
        height: "auto",
        alignItems: "flex-end",
        justifyContent: "flex-start",
        gridGap: "10px",
        maxWidth: "60rem",
        width: "100%",
        marginRight: "auto",
        marginLeft: "auto",
      }}
    >
      <Box sx={{ marginBottom: "45px", flexShrink: 0 }}>
        <Avatar sender="other" agentName={message?.agent_name} />
      </Box>

      <Column
        sx={{
          width: "fit-content",
          maxWidth: "100%",
          minWidth: 0,
          marginRight: "42px",
          "&:hover [data-message-actions]": { opacity: 1 },
        }}
      >
        {/* Message text */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            gap: theme.spacing(1.25),
            padding: theme.spacing(1.5, 2),
            borderRadius: theme.spacing(1.5),
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.body2.fontSize,
            fontWeight: 500,
            lineHeight: 1.4,
            width: "fit-content",
            minWidth: 0,
            maxWidth: "min(56.7rem, 100%)",
            position: "relative",
          }}
        >
          {contentParts.length > 0 && !hideLetterMessageText ? (
            <Box
              id={`markdown-preview-${message.id}`}
              sx={{ width: "100%", minWidth: 0, maxWidth: "100%" }}
            >
              {contentParts.map((part, index) =>
                part.type === "markdown" ? (
                  <MarkdownRenderer
                    key={`${message.id}-part-${index}`}
                    content={part.content}
                    metadata={message?.metadata}
                    id={message.id}
                  />
                ) : (
                  renderCardPart(
                    message.agent_name,
                    part.content,
                    message?.metadata,
                    `${message.id}-part-${index}`,
                  )
                ),
              )}
            </Box>
          ) : null}

          {letter ? (
            <ChatMessageLetterBox letter={letter} />
          ) : null}

          {showLetterPlaceholder ? (
            <Skeleton
              variant="rounded"
              width={240}
              height={25}
              sx={{ maxWidth: "100%" }}
            />
          ) : null}

          {/* REV-2299: "message delivered but nothing visible" safety net —
              no parsed content parts and no letter means this bubble would
              otherwise render completely empty. */}
          {/* A letter's message can have no text: while the letters are still
              loading it is not "unavailable", just not matched yet. */}
          {contentParts.length === 0 && !letter && !chatStore.isChatLettersLoading ? (
            <ChatMessageUnavailable raw={message?.data} />
          ) : null}
        </Box>

        <ChatMessageActions
          message={message}
          isCrossUserMessage={isCrossUserMessage}
        />
      </Column>
    </Row>
  );
});

export default ChatServerMessage;
